import { CircularProgress, FormHelperText, withStyles } from '@material-ui/core';
import React, { useRef, useState } from 'react';
import useSnowflakeProperties from '../../../../hooks/useSnowflakeProperties';
import SnowflakeProperties from '../../../../models/snowflakeProperties';
import { Formik, FormikConsumer } from 'formik';
import * as Yup from 'yup';
import PriorityTwoFailure from '../../../announcement/priority-two-failure';
import Paths from '../../../../models/enums/paths';
import { useHistory } from 'react-router';
import PrimaryButton from '../../../common/primary-button';
import TertiaryButton from '../../../common/tertiary-button';
import * as commonStyles from '../../../common/common.module.scss';
import OutlinedInput from '../../../common/outlined-input';
import NavigationWarning from '../../navigationWarning';

interface SnowflakeProps {
  clientId: number;
}

const SnowflakeSchema = Yup.object().shape({
  snowflakeAccountName: Yup.string()
    .matches(/^[^;]+$/, 'Snowflake account name cannot have a semicolon')
    .required('Snowflake account name is a required field.')
    .max(50, 'Snowflake account name must be less than 50 characters'),
  snowflakeDatabaseName: Yup.string()
    .matches(/^[^;]+$/, 'Snowflake database name cannot have a semicolon')
    .required('Snowflake database name is a required field.')
    .max(50, 'Snowflake database name must be less than 50 characters'),
  snowflakeRoleName: Yup.string()
    .matches(/^[^;]+$/, 'Snowflake role name cannot have a semicolon')
    .required('Snowflake role name is a required field.')
    .max(50, 'Snowflake role name must be less than 50 characters'),
  snowflakeWarehouseName: Yup.string()
    .matches(/^[^;]+$/, 'Snowflake warehouse name cannot have a semicolon')
    .max(50, 'Snowflake warehouse name must be less than 50 characters')
    .nullable()
});

const SnowflakeInfo = withStyles({
  root: {
    fontSize: 10,
    margin: '-5px 15px 0'
  }
})(FormHelperText);

const Snowflake = ({ clientId }: SnowflakeProps) => {
  const { fetchSnowflakeProperties, editSnowflakeProperties } = useSnowflakeProperties();
  const [loading, setLoading] = React.useState<boolean>(true);

  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const formikRef = useRef(null);
  const unblock = useRef(null);
  const history = useHistory();

  const [snowflakeProperties, setSnowflakeProperties] = useState<SnowflakeProperties>({
    snowflakeAccountName: '',
    snowflakeDatabaseName: '',
    snowflakeRoleName: '',
    snowflakeWarehouseName: ''
  });
  const [displayDirtyWarning, setDisplayDirtyWarning] = React.useState<boolean>(false);
  const [confirmedNavigation, setConfirmedNavigation] = React.useState<boolean>(false);
  const [blockedLocation, setBlockedLocation] = React.useState<string>('');

  React.useEffect(() => {
    fetchSnowflakeProperties(clientId.toString()).then((snowflakeProperties) => {
      setSnowflakeProperties({
        snowflakeAccountName: snowflakeProperties.snowflakeAccountName ?? '',
        snowflakeDatabaseName: snowflakeProperties.snowflakeDatabaseName ?? '',
        snowflakeRoleName: snowflakeProperties.snowflakeRoleName ?? '',
        snowflakeWarehouseName: snowflakeProperties.snowflakeWarehouseName ?? ''
      });
      setLoading(false);
    });

    unblock.current = history.block((location) => {
      if (formikRef.current.dirty) {
        setDisplayDirtyWarning(true);
        setBlockedLocation(location.pathname);
        return false;
      } else {
        unblock.current();
      }
    });
  }, []);

  React.useEffect(() => {
    if (confirmedNavigation) {
      unblock.current();
      history.push(blockedLocation);
    }
  }, [confirmedNavigation]);

  const handleCancel = (dirty: boolean) => {
    history.push(Paths.Clients);
  };

  if (loading) {
    return (
      <div className="loadingIndicator">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      {errorMessage && <PriorityTwoFailure message={errorMessage} />}
      <h1>Edit Snowflake Configuration</h1>
      <Formik
        innerRef={formikRef}
        initialValues={snowflakeProperties}
        validationSchema={SnowflakeSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          try {
            const response = await editSnowflakeProperties(clientId.toString(), values);
            if (!response.success) {
              throw Error(`Failed to update Snowflake properties: ${response.message}`);
            }
            setSubmitting(false);
            unblock.current();
            history.push(Paths.Clients);
          } catch (err) {
            if (err instanceof Error) {
              setErrorMessage(err.message);
            } else {
              setErrorMessage(err.responseJSON.message);
            }
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, dirty }) => (
          <form>
            <FormikConsumer>
              {(formik) => (
                <NavigationWarning
                  formik={formik}
                  displayDirtyWarning={displayDirtyWarning}
                  confirmNavigationWarning={() => {
                    setConfirmedNavigation(true);
                    history.push(blockedLocation);
                  }}
                  cancleNavigationWarning={() => setDisplayDirtyWarning(false)}
                />
              )}
            </FormikConsumer>
            {isSubmitting && (
              <div className={commonStyles.overlay}>
                <div className={commonStyles.fixedCircularProgress}>
                  <CircularProgress />
                </div>
              </div>
            )}
            <fieldset className={commonStyles.fieldset}>
              <OutlinedInput
                id="snowflakeAccountName"
                label="Snowflake Account Name"
                name="snowflakeAccountName"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.snowflakeAccountName ?? ''}
                error={errors.snowflakeAccountName && touched.snowflakeAccountName}
                helperText={errors.snowflakeAccountName && touched.snowflakeAccountName && errors.snowflakeAccountName}
                required
              />
              <SnowflakeInfo>Snowflake Account Locator where customer database is located</SnowflakeInfo>
              <OutlinedInput
                id="snowflakeRoleName"
                label="Snowflake Role Name"
                name="snowflakeRoleName"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.snowflakeRoleName ?? ''}
                error={errors.snowflakeRoleName && touched.snowflakeRoleName}
                helperText={errors.snowflakeRoleName && touched.snowflakeRoleName && errors.snowflakeRoleName}
                required
              />
              <SnowflakeInfo>
                {'Snowflake role which is set up for the portal to query data.  E.g. <Client><Env>_PORTAL'}
              </SnowflakeInfo>
              <OutlinedInput
                id="snowflakeDatabaseName"
                label="Snowflake Database Name"
                name="snowflakeDatabaseName"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.snowflakeDatabaseName ?? ''}
                error={errors.snowflakeDatabaseName && touched.snowflakeDatabaseName}
                helperText={
                  errors.snowflakeDatabaseName && touched.snowflakeDatabaseName && errors.snowflakeDatabaseName
                }
                required
              />
              <SnowflakeInfo>Snowflake Database where customer data is</SnowflakeInfo>
              <OutlinedInput
                id="snowflakeWarehouseName"
                label="Snowflake Warehouse Name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.snowflakeWarehouseName ?? ''}
                error={errors.snowflakeWarehouseName && touched.snowflakeWarehouseName}
                helperText={
                  errors.snowflakeWarehouseName && touched.snowflakeWarehouseName && errors.snowflakeWarehouseName
                }
              />
              <SnowflakeInfo>Snowflake warehouse used for compute by the portal</SnowflakeInfo>
            </fieldset>
            <div className={commonStyles.formActions}>
              <TertiaryButton
                text="Cancel"
                title="Cancel"
                handleClick={() => {
                  handleCancel(dirty);
                }}
              />
              <span className={commonStyles.submitButton}>
                <PrimaryButton text="Save" title="Save" deactive={isSubmitting} handleClick={handleSubmit} />
              </span>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default Snowflake;
